import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Loader2, PackageSearch, Settings } from 'lucide-react';

import { InventoryAdjustmentApi } from 'api/inventoryAdjustment';
import { WarehouseInventoryV2Api } from 'api/warehouseInventoryV2';
import { UserContext } from 'contexts/UserContext';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent } from 'shadcn/components/ui/Card';
import { Tooltip, TooltipContent, TooltipTrigger } from 'shadcn/components/ui/tooltip';
import { HandsontableDataGrid } from 'shared/components/dataTableHandson';
import { ExportButton } from 'shared/components/ExportCSVButton';
import { Notifications } from 'shared/components/Notifications';
import dayjs from 'utils/dayjsConfig';

import { AddBulkShrinkModal } from './AddBulkShrinkModal';
import { ItemsFromWeekSelector } from './ItemsFromWeekSelector';
import { ShrinkShipmentModal } from './ShrinkShipmentModal';

const warehouseApi = new WarehouseInventoryV2Api();
const inventoryAdjustmentApi = new InventoryAdjustmentApi();

const captureServerEntry = (entry) => ({
    category: entry.category,
    externalSku: String(entry.externalSku ?? ''),
    itemName: entry.itemName,
    notes: entry.notes,
    qtyUnits: entry.qtyUnits,
    reason: entry.reason,
});

const buildServerEntriesMap = (rows) => Object.fromEntries(rows.map((row) => [row.id, captureServerEntry(row)]));

function PendingTab() {
    const { user } = useContext(UserContext);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [isOpen, setIsOpen] = useState(false);
    const [isWeekSelectorOpen, setIsWeekSelectorOpen] = useState(false);
    const [allEntries, setAllEntries] = useState([]);
    const [totalPendingEntries, setTotalPendingEntries] = useState(0);
    const [totalUnits, setTotalUnits] = useState(0);
    const [totalCost, setTotalCost] = useState(0);
    const [selectedRequests, setSelectedRequests] = useState([]);
    const [selectedRequestIds, setSelectedRequestIds] = useState([]);
    const [selectedSummaryString, setSelectedSummaryString] = useState('');
    const [itemInformation, setItemInformation] = useState([]);
    const [reasonCodesByCategory, setReasonCodesByCategory] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    const [searchIndexes, setSearchIndexes] = useState(null);
    const [noItemsWarning, setNoItemsWarning] = useState(false);
    const [isShrinkShipmentModalOpen, setIsShrinkShipmentModalOpen] = useState(false);
    const hotInstanceRef = useRef(null);
    const serverEntriesRef = useRef({});

    const columnHeaders = [
        'Sku',
        'Item Name',
        'Company Source',
        'Units',
        'Cost (Estimated)',
        'Category',
        'Reason Code',
        'Notes',
        'Shipment ID',
        'Username',
        'Date Entered',
        'Status',
    ];

    const columns = [
        {
            data: 'externalSku',
            type: 'autocomplete',
            source: (query, callback) => {
                const results = handleSearch(query);
                callback(results.map((item) => item.sku));
            },
            strict: false,
            width: 208.33,
        },
        {
            data: 'itemName',
            type: 'autocomplete',
            source: (query, callback) => {
                const results = handleSearch(query);
                callback(results.map((item) => item.itemName));
            },
            strict: false,
            width: 208.33,
        },
        {
            data: 'companySourceName',
            type: 'text',
            readOnly: true,
            width: 158.33,
        },
        { data: 'qtyUnits', type: 'numeric', readOnly: false, allowInvalid: false, width: 115.33 },
        {
            data: 'cost',
            type: 'numeric',
            numericFormat: { pattern: '$0,0.00', culture: 'en-US' },
            readOnly: true,
            allowInvalid: false,
            width: 208.33,
        },
        { data: 'category', type: 'text', readOnly: true, width: 208.33 },
        {
            data: 'reason',
            type: 'dropdown',
            source(query, callback) {
                try {
                    const rowIndex = this?.row;
                    const category = this?.instance?.getDataAtRowProp(rowIndex, 'category');
                    const reasons = getReasonsForCategory(category);
                    callback(Array.isArray(reasons) ? reasons : []);
                } catch (e) {
                    callback([]);
                }
            },
            width: 208.33,
            strict: true,
            allowInvalid: false,
            filter: false,
        },
        { data: 'notes', type: 'text', readOnly: false, width: 208.33 },
        { data: 'shipmentId', type: 'text', readOnly: true, width: 208.33 },
        { data: 'createdBy', type: 'text', readOnly: true, width: 208.33 },
        { data: 'createdOn', type: 'date', readOnly: true, allowInvalid: false, width: 208.33 },
        { data: 'status', type: 'text', readOnly: true, width: 208.33 },
    ];

    const getPendingBulkShrinkRequests = async (setLoading = false) => {
        if (setLoading) {
            setIsLoading(true);
        }
        const result = await inventoryAdjustmentApi.getPendingBulkShrinkRequests(user.facilityID);
        if (!result.status) {
            setNotification({ text: 'Error fetching pending bulk shrink requests', level: 'error' });
            return;
        }
        const sortedRows = result.data.rows.sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
        serverEntriesRef.current = buildServerEntriesMap(sortedRows);
        setAllEntries(sortedRows);
        // Clear any selections after data reload so stale selections don't persist
        setSelectedRequests([]);
        setSelectedRequestIds([]);
        if (setLoading) {
            setIsLoading(false);
        }
    };

    const getItems = async (week, year) => {
        setIsLoading(true);
        let targetWeek = week;
        let targetYear = year;
        if (targetWeek == null || targetYear == null) {
            targetWeek = dayjs().isoWeek();
            targetYear = dayjs().isoWeekYear();
        }
        try {
            const result = await inventoryAdjustmentApi.getItemsForFacilityWeekYear(
                user.facilityID,
                targetWeek,
                targetYear,
            );
            if (!result.status) {
                setNotification({ text: 'Error fetching item information', level: 'error' });
                return;
            }
            const items = await mapItems(result.data?.items ?? []);
            if (items.length === 0) {
                setNoItemsWarning(true);
            } else {
                setNoItemsWarning(false);
            }
            setItemInformation(items);
        } finally {
            setIsLoading(false);
        }
    };

    const mapItems = async (ii) => {
        const items = ii.reduce((acc, item) => {
            if (!item.name || !item.externalSku) {
                return acc;
            }
            let category;
            if (item.l1TaxonomyId === 1) {
                category = 'PRODUCE';
            } else if (item.l1TaxonomyId === 1871) {
                category = 'FBM';
            } else if (item.l1TaxonomyId > 1 && item.l1TaxonomyId !== 1871) {
                category = 'GROCERY';
            } else {
                return acc; // catches null, 0, undefined, negatives, etc.
            }

            acc.push({
                sku: item.externalSku,
                itemName: item.name,
                category,
            });

            return acc;
        }, []);

        // Create search indexes after mapping items
        setSearchIndexes(createSearchIndexes(items));
        return items;
    };

    const createSearchIndexes = (items) => {
        const skuMap = new Map();
        const nameIndex = new Map();

        items.forEach((item) => {
            // Convert SKU to string for consistent handling
            const skuString = String(item.sku);
            skuMap.set(skuString, item);

            // Index by name substrings
            const words = item.itemName.toLowerCase().split(' ');
            words.forEach((word) => {
                if (!nameIndex.has(word)) {
                    nameIndex.set(word, new Set());
                }
                nameIndex.get(word).add(item);
            });
        });

        return { skuMap, nameIndex };
    };

    const searchItems = (query, indexes) => {
        if (!query) return [];

        const { nameIndex, skuMap } = indexes;
        const searchTerm = String(query).trim();

        if (skuMap.has(searchTerm)) {
            return [skuMap.get(searchTerm)];
        }

        const results = new Set();

        for (const [skuString, item] of skuMap.entries()) {
            if (skuString.startsWith(searchTerm)) {
                results.add(item);
            }
        }

        const searchWords = searchTerm.toLowerCase().split(' ');
        searchWords.forEach((word) => {
            nameIndex.forEach((items, indexWord) => {
                if (indexWord.includes(word)) {
                    items.forEach((item) => results.add(item));
                }
            });
        });

        return Array.from(results);
    };

    const handleSearch = (query) => {
        if (!query || !searchIndexes) return [];
        return searchItems(query, searchIndexes);
    };

    const getReasonCodes = async () => {
        const result = await warehouseApi.getAdjustReasons(null, true);
        if (!result.msg == 'Success' || !result.data?.reasons) {
            setNotification({ text: 'Error fetching reason codes', level: 'error' });
            return;
        }
        setReasonCodesByCategory(result.data.reasons);
    };

    useEffect(() => {
        getPendingBulkShrinkRequests();
        getItems();
        getReasonCodes();
    }, []);

    useEffect(() => {
        setTotalPendingEntries(allEntries.length);
        setTotalUnits(allEntries.reduce((acc, entry) => acc + entry.qtyUnits, 0));
        setTotalCost(allEntries.reduce((acc, entry) => acc + Number(entry?.cost ?? 0), 0));
    }, [allEntries]);

    useEffect(() => {
        handleSelectedSummaryString();
    }, [selectedRequests]);

    const handleOpenChange = (open, reload = false) => {
        if (reload) {
            getPendingBulkShrinkRequests(reload);
        }
        setIsOpen(open);
    };

    const handleShrinkShipmentModalOpenChange = (open, reload = false) => {
        if (reload) {
            getPendingBulkShrinkRequests(reload);
        }
        setIsShrinkShipmentModalOpen(open);
    };

    const handleSelectedSummaryString = () => {
        if (selectedRequests.length > 0) {
            const entries = `${selectedRequests.length} entries selected    `;
            const totalUnitsSelected = selectedRequests.reduce((acc, entry) => acc + Number(entry?.qtyUnits ?? 0), 0);

            const totalCostSelected = selectedRequests.reduce((acc, entry) => acc + Number(entry?.cost ?? 0), 0);
            const units = `${totalUnitsSelected} units`;
            const cost = `${totalCostSelected.toFixed(2)}`;
            setSelectedSummaryString(`${entries} | ${units} | $${cost}`);
        } else {
            setSelectedSummaryString('');
        }
    };

    const handleSelectionChange = (ids, rows) => {
        setSelectedRequestIds(ids);
        setSelectedRequests(rows);
    };

    const handleUpdateEntry = useCallback(async (rowData, prop, _oldValue, newValue) => {
        try {
            const result = await inventoryAdjustmentApi.updatePendingBulkShrinkRequest(rowData.id, prop, newValue);
            if (!result.status) {
                setNotification({
                    text: result.msg || 'Failed to update entry. Reverting your change.',
                    level: 'error',
                });
                return false;
            }
            setNotification({ text: `Successfully updated ${prop}`, level: 'success' });
            return true;
        } catch (e) {
            if (e?.name === 'AbortError') {
                throw e;
            }
            setNotification({
                text: e?.message || 'Failed to update entry. Reverting your change.',
                level: 'error',
            });
            return false;
        }
    }, []);

    const handleApproveEntriesByIds = useCallback(async (ids) => {
        setIsApproving(true);
        const result = await inventoryAdjustmentApi.approveBulkShrinkRequests(ids);
        if (!result.status) {
            setNotification({ text: 'Error approving bulk shrink requests', level: 'error' });
            setIsApproving(false);
            return;
        }
        const successCount = result?.data?.success?.length ?? 0;
        const failedCount = result?.data?.failed?.length ?? 0;

        if (successCount === 0 && failedCount > 0) {
            setNotification({ text: 'Error approving bulk shrink requests', level: 'error' });
            setIsApproving(false);
            return;
        }
        if (successCount > 0 && failedCount > 0) {
            setNotification({
                text: 'Some bulk shrink requests were approved, but some were rejected',
                level: 'warning',
            });
        } else {
            setNotification({ text: 'Bulk shrink requests approved', level: 'success' });
        }
        getPendingBulkShrinkRequests(true);
        setIsApproving(false);
    }, []);

    const handleRejectEntriesByIds = useCallback(async (ids) => {
        setIsRejecting(true);
        const result = await inventoryAdjustmentApi.rejectBulkShrinkRequests(ids);
        if (!result.status) {
            setNotification({ text: 'Error rejecting bulk shrink requests', level: 'error' });
            setIsRejecting(false);
            return;
        }
        setNotification({ text: 'Bulk shrink requests rejected', level: 'success' });
        getPendingBulkShrinkRequests(true);
        setIsRejecting(false);
    }, []);

    const getReasonsForCategory = useCallback(
        (category) => reasonCodesByCategory[category] || [],
        [reasonCodesByCategory],
    );

    const findVisualRowByEntryId = useCallback((hot, entryId) => {
        const totalRows = hot.countRows?.() ?? 0;
        for (let visualRow = 0; visualRow < totalRows; visualRow += 1) {
            const physicalRow = hot.toPhysicalRow ? hot.toPhysicalRow(visualRow) : visualRow;
            const rowData = hot.getSourceDataAtRow(physicalRow);
            if (rowData?.id === entryId) {
                return visualRow;
            }
        }
        return null;
    }, []);

    const revertEntryToServer = useCallback(
        (hot, row, entryId) => {
            const serverEntry = serverEntriesRef.current[entryId];
            if (!serverEntry) {
                return;
            }

            const visualRow = findVisualRowByEntryId(hot, entryId) ?? row;
            const physicalRow = hot.toPhysicalRow ? hot.toPhysicalRow(visualRow) : visualRow;
            const rowData = hot.getSourceDataAtRow(physicalRow);

            if (rowData) {
                rowData.itemName = serverEntry.itemName;
                rowData.externalSku = serverEntry.externalSku;
                rowData.category = serverEntry.category;
                rowData.reason = serverEntry.reason;
            }

            hot.setDataAtRowProp(visualRow, 'itemName', serverEntry.itemName, 'auto-update');
            hot.setDataAtRowProp(visualRow, 'externalSku', serverEntry.externalSku, 'auto-update');
            hot.setDataAtRowProp(visualRow, 'category', serverEntry.category, 'auto-update');
            hot.setDataAtRowProp(visualRow, 'reason', serverEntry.reason, 'auto-update');
            hot.render();
        },
        [findVisualRowByEntryId],
    );

    const afterChange = useCallback(
        function (changes, source) {
            if (!changes || source === 'loadData' || source === 'updateData' || source === 'formatDate') {
                return;
            }

            if (source !== 'auto-update') {
                const hot = this;
                (async () => {
                    for (const [row, prop, oldValue, newValue] of changes) {
                        const physicalRow = hot?.toPhysicalRow ? hot.toPhysicalRow(row) : row;
                        const sourceRowIndex = typeof physicalRow === 'number' ? physicalRow : row;
                        const entryId = hot.getSourceDataAtRow(sourceRowIndex)?.id;
                        if (newValue == null || newValue === '') continue;
                        // Skip if the new value is the same as the old value
                        if (newValue === oldValue) continue;

                        if (prop === 'itemName' || prop === 'externalSku') {
                            const results = handleSearch(newValue);
                            const exact = results.find(
                                (i) => i.itemName === newValue || String(i.sku) === String(newValue),
                            );

                            if (exact) {
                                const serverEntry = entryId != null ? serverEntriesRef.current[entryId] : null;
                                const gridCategory = hot.getDataAtRowProp(row, 'category');
                                const currentCategory = gridCategory || serverEntry?.category;

                                // Block if the change would alter the category (only when a current category exists)
                                if (
                                    currentCategory &&
                                    exact.category &&
                                    String(exact.category) !== String(currentCategory)
                                ) {
                                    if (entryId != null) {
                                        revertEntryToServer(hot, row, entryId);
                                    }
                                    setNotification({
                                        text: 'Changing item or SKU to a different category is prohibited.',
                                        level: 'error',
                                    });
                                    continue;
                                }

                                // Apply linked field updates (safe: same category or no current category)
                                if (prop === 'itemName' && exact.itemName === newValue) {
                                    hot.setDataAtRowProp(row, 'externalSku', exact.sku, 'auto-update');
                                    hot.setDataAtRowProp(row, 'category', exact.category, 'auto-update');
                                } else if (prop === 'externalSku' && String(exact.sku) === String(newValue)) {
                                    hot.setDataAtRowProp(row, 'itemName', exact.itemName, 'auto-update');
                                    hot.setDataAtRowProp(row, 'category', exact.category, 'auto-update');
                                }

                                // Trigger update callback for the valid user edit
                                const updatedRowData = hot.getSourceDataAtRow(sourceRowIndex);
                                const newExternalSku = String(exact.sku);
                                const previousExternalSku = serverEntry?.externalSku ?? String(oldValue ?? '');
                                let success = true;
                                const colIndexForMeta = hot.propToCol('externalSku');
                                // Mark cell as updating and re-render
                                hot.setCellMeta(row, colIndexForMeta, 'isUpdating', true);
                                hot.render();
                                try {
                                    const result = await handleUpdateEntry(
                                        updatedRowData,
                                        'externalSku',
                                        previousExternalSku,
                                        newExternalSku,
                                    );
                                    // Treat undefined return as success for now
                                    success = result === undefined ? true : Boolean(result);
                                } catch (e) {
                                    success = false;
                                } finally {
                                    hot.setCellMeta(row, colIndexForMeta, 'isUpdating', false);
                                    hot.render();
                                }

                                if (!success) {
                                    if (entryId != null) {
                                        revertEntryToServer(hot, row, entryId);
                                    }
                                } else if (entryId != null) {
                                    serverEntriesRef.current[entryId] = captureServerEntry(
                                        hot.getSourceDataAtRow(sourceRowIndex),
                                    );
                                }
                            }
                        } else {
                            // For non-item fields, pass through the original change
                            const updatedRowData = hot.getSourceDataAtRow(sourceRowIndex);
                            let success = true;
                            const colIndexForMeta = hot.propToCol(prop);
                            hot.setCellMeta(row, colIndexForMeta, 'isUpdating', true);
                            hot.render();
                            try {
                                const result = await handleUpdateEntry(updatedRowData, prop, oldValue, newValue);
                                success = result === undefined ? true : Boolean(result);
                            } catch (e) {
                                success = false;
                            } finally {
                                hot.setCellMeta(row, colIndexForMeta, 'isUpdating', false);
                                hot.render();
                            }
                            if (!success) {
                                const serverValue =
                                    entryId != null ? serverEntriesRef.current[entryId]?.[prop] : oldValue;
                                const visualRow = entryId != null ? (findVisualRowByEntryId(hot, entryId) ?? row) : row;
                                const physicalRowForRevert = hot.toPhysicalRow
                                    ? hot.toPhysicalRow(visualRow)
                                    : visualRow;
                                const rowData = hot.getSourceDataAtRow(physicalRowForRevert);
                                if (rowData) {
                                    rowData[prop] = serverValue;
                                }
                                hot.setDataAtRowProp(visualRow, prop, serverValue, 'auto-update');
                                hot.render();
                            } else if (entryId != null && serverEntriesRef.current[entryId]) {
                                serverEntriesRef.current[entryId][prop] = newValue;
                            }
                        }
                    }
                })();
            }
        },
        [findVisualRowByEntryId, handleSearch, handleUpdateEntry, revertEntryToServer],
    );

    return (
        <div>
            <Notifications options={notification} />
            <div className="w-full max-w-[1200px] mb-4 mx-auto">
                <div className="flex items-center justify-start mb-2 gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Select week and year"
                        onClick={() => setIsWeekSelectorOpen(true)}
                    >
                        <Settings className="h-5 w-5" />
                    </Button>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setIsShrinkShipmentModalOpen(true)}
                                disabled={isLoading}
                                aria-label="Shrink a Shipment"
                            >
                                {isLoading ? (
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                ) : (
                                    <PackageSearch className="h-5 w-5" />
                                )}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Shrink a Shipment</TooltipContent>
                    </Tooltip>
                    {noItemsWarning && (
                        <div className="flex items-center" role="alert">
                            <AlertTriangle className="h-4 w-4 text-red-600 mr-2" aria-hidden="true" />
                            <p className="text-sm text-red-600">
                                No items found for the selected week and year. Please select a different week and year.
                            </p>
                        </div>
                    )}
                </div>
                <div className="flex flex-row flex-wrap gap-4 mb-4">
                    <Card className="flex-1">
                        <CardContent>
                            <div className="flex flex-col gap-4 justify-center items-center">
                                <h1 className="text-2xl font-bold">{totalPendingEntries}</h1>
                                <h2 className="text-sm text-gray-500">Pending Entries</h2>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="flex-1">
                        <CardContent>
                            <div className="flex flex-col gap-4 justify-center items-center">
                                <h1 className="text-2xl font-bold">{totalUnits}</h1>
                                <h2 className="text-sm text-gray-500">Total Units</h2>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="flex-1">
                        <CardContent>
                            <div className="flex flex-col gap-4 justify-center items-center">
                                <h1 className="text-2xl font-bold">${totalCost.toFixed(2)}</h1>
                                <h2 className="text-sm text-gray-500">Total Cost</h2>
                            </div>
                        </CardContent>
                    </Card>
                </div>
                <div className="flex flex-row justify-between gap-4">
                    <Button onClick={() => setIsOpen(true)} disabled={isLoading}>
                        {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Add Entries'}
                    </Button>
                    <div className="flex gap-4">
                        <h2 className="text-sm text-gray-500">{selectedSummaryString}</h2>
                        <Button
                            disabled={selectedRequests.length === 0 || isApproving}
                            onClick={() => handleApproveEntriesByIds(selectedRequestIds)}
                        >
                            {isApproving ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Approving...
                                </>
                            ) : (
                                'Approve Selected Entries'
                            )}
                        </Button>
                        <Button
                            disabled={selectedRequests.length === 0 || isRejecting}
                            onClick={() => handleRejectEntriesByIds(selectedRequestIds)}
                        >
                            {isRejecting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Rejecting...
                                </>
                            ) : (
                                'Reject Selected Entries'
                            )}
                        </Button>
                        <ExportButton
                            data={allEntries}
                            columnHeaders={columnHeaders}
                            columns={columns}
                            disabled={allEntries.length === 0}
                            filename="shrink-log-pending-entries"
                        />
                    </div>
                </div>
            </div>
            <div>
                {isLoading ? (
                    <Card className="w-full max-w-[1200px] mx-auto">
                        <CardContent className="flex flex-col items-center justify-center py-8">
                            <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        {allEntries.length === 0 ? (
                            <Card className="w-full max-w-[1200px] mx-auto">
                                <CardContent className="flex flex-col items-center justify-center py-8">
                                    <p className="text-gray-500 text-lg">No pending bulk shrink requests to show</p>
                                    <p className="text-gray-400 text-sm mt-2">
                                        Click Add Entries to create new bulk shrink requests
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="w-full overflow-x-auto">
                                <HandsontableDataGrid
                                    data={allEntries}
                                    selectable
                                    rowKey="id"
                                    selectedRows={selectedRequestIds}
                                    onSelectionChange={handleSelectionChange}
                                    hotInstanceRef={hotInstanceRef}
                                    colHeaders={columnHeaders}
                                    columns={columns}
                                    afterChange={afterChange}
                                    handsontableSettings={{
                                        height: 400,
                                        manualColumnSize: false,
                                        rowHeaders: false,
                                        contextMenu: {
                                            items: {
                                                approve: {
                                                    name: 'Approve',
                                                    callback: (key, selection) => {
                                                        const visualRow = selection[0]?.start?.row;
                                                        const physicalRow =
                                                            typeof visualRow === 'number' && hotInstanceRef.current
                                                                ? hotInstanceRef.current.toPhysicalRow(visualRow)
                                                                : undefined;
                                                        const rowData =
                                                            typeof visualRow === 'number'
                                                                ? hotInstanceRef.current?.getSourceDataAtRow(
                                                                      physicalRow,
                                                                  )
                                                                : undefined;
                                                        if (rowData?.id != null)
                                                            handleApproveEntriesByIds([rowData.id]);
                                                    },
                                                    disabled: false,
                                                },
                                                separator1: '---------',
                                                reject: {
                                                    name: 'Reject',
                                                    callback: (key, selection) => {
                                                        const visualRow = selection[0]?.start?.row;
                                                        const physicalRow =
                                                            typeof visualRow === 'number' && hotInstanceRef.current
                                                                ? hotInstanceRef.current.toPhysicalRow(visualRow)
                                                                : undefined;
                                                        const rowData =
                                                            typeof visualRow === 'number'
                                                                ? hotInstanceRef.current?.getSourceDataAtRow(
                                                                      physicalRow,
                                                                  )
                                                                : undefined;
                                                        if (rowData?.id != null) handleRejectEntriesByIds([rowData.id]);
                                                    },
                                                    disabled: false,
                                                },
                                            },
                                        },
                                    }}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
            <ShrinkShipmentModal
                isOpen={isShrinkShipmentModalOpen}
                onOpenChange={handleShrinkShipmentModalOpenChange}
            />
            <AddBulkShrinkModal
                isOpen={isOpen}
                onOpenChange={handleOpenChange}
                getReasonsForCategory={getReasonsForCategory}
                searchItems={handleSearch}
                adjustmentApi={inventoryAdjustmentApi}
            />
            <ItemsFromWeekSelector
                isOpen={isWeekSelectorOpen}
                onOpenChange={setIsWeekSelectorOpen}
                onSubmit={({ week, year }) => getItems(week, year)}
            />
        </div>
    );
}

export { PendingTab };
